import { useState, useCallback, useEffect } from 'react';
import { useAccount, useContractWrite, usePrepareContractWrite, useWaitForTransaction } from 'wagmi';
import { parseEther } from 'viem';
import { toast } from 'sonner';
import { ipfsService, type NFTMetadata } from '@/services/ipfsService';
import { NFT_CONTRACT_ABI } from '@/config/nftContract';

interface NFTMintingState {
  isUploading: boolean;
  isMinting: boolean;
  uploadProgress: number;
  mintProgress: number;
  error: string | null;
}

interface NFTSuccessData {
  transactionHash: string;
  contractAddress: string;
  imageUrl: string;
  metadataUrl: string;
  name: string;
  description?: string;
  mintPrice: string;
  platformFee: string;
}

interface MintNFTParams {
  file: File;
  metadata: Omit<NFTMetadata, 'image'>;
  contractAddress: string;
  mintPrice: string; // in ETH/BNB
}

interface BatchMintNFTParams {
  files: File[];
  baseMetadata: Omit<NFTMetadata, 'image'> & { baseName?: string };
  contractAddress: string;
  mintPrice: string; // in ETH/BNB per NFT
}

export function useNFTMinting() {
  const { address } = useAccount();
  const [state, setState] = useState<NFTMintingState>({
    isUploading: false,
    isMinting: false,
    uploadProgress: 0,
    mintProgress: 0,
    error: null,
  });
  const [successData, setSuccessData] = useState<NFTSuccessData | null>(null);

  // Single NFT minting
  const [mintParams, setMintParams] = useState<{
    to: string;
    tokenURI: string;
    value: bigint;
    contractAddress: string;
  } | null>(null);

  const { config: mintConfig } = usePrepareContractWrite({
    address: mintParams?.contractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'mintNFT',
    args: mintParams ? [mintParams.to as `0x${string}`, mintParams.tokenURI] : undefined,
    value: mintParams?.value,
    enabled: Boolean(mintParams && address),
  });

  const { write: mintWrite, data: mintHash, error: mintError } = useContractWrite(mintConfig);

  // Auto-execute mint when write is ready
  useEffect(() => {
    if (mintWrite && mintParams) {
      mintWrite();
      setMintParams(null); // Clear params after execution
    }
  }, [mintWrite, mintParams]);

  const { isLoading: isMintTxLoading } = useWaitForTransaction({
    hash: mintHash?.hash,
    onSuccess: () => {
      toast.success('NFT minted successfully!');
      setState(prev => ({ ...prev, isMinting: false, mintProgress: 100 }));
    },
    onError: (error) => {
      console.error('Mint transaction failed:', error);
      toast.error('Minting failed');
      setState(prev => ({ ...prev, isMinting: false, error: error.message }));
    },
  });

  // Batch NFT minting
  const [batchMintParams, setBatchMintParams] = useState<{
    to: string;
    tokenURIs: string[];
    value: bigint;
    contractAddress: string;
  } | null>(null);

  const { config: batchMintConfig } = usePrepareContractWrite({
    address: batchMintParams?.contractAddress as `0x${string}`,
    abi: NFT_CONTRACT_ABI,
    functionName: 'batchMintNFT',
    args: batchMintParams ? [batchMintParams.to as `0x${string}`, batchMintParams.tokenURIs] : undefined,
    value: batchMintParams?.value,
    enabled: Boolean(batchMintParams && address),
  });

  const { write: batchMintWrite, data: batchMintHash, error: batchMintError } = useContractWrite(batchMintConfig);

  // Auto-execute batch mint when write is ready
  useEffect(() => {
    if (batchMintWrite && batchMintParams) {
      batchMintWrite();
      setBatchMintParams(null); // Clear params after execution
    }
  }, [batchMintWrite, batchMintParams]);

  const { isLoading: isBatchMintTxLoading } = useWaitForTransaction({
    hash: batchMintHash?.hash,
    onSuccess: () => {
      toast.success('Batch NFTs minted successfully!');
      setState(prev => ({ ...prev, isMinting: false, mintProgress: 100 }));
    },
    onError: (error) => {
      console.error('Batch mint transaction failed:', error);
      toast.error('Batch minting failed');
      setState(prev => ({ ...prev, isMinting: false, error: error.message }));
    },
  });

  /**
   * Mint a single NFT
   */
  const mintNFT = useCallback(async (params: MintNFTParams) => {
    if (!address) {
      toast.error('Please connect your wallet');
      return;
    }

    try {
      setState(prev => ({ ...prev, isUploading: true, uploadProgress: 0, error: null }));

      // Test IPFS connection first
      const isConnected = await ipfsService.testConnection();
      if (!isConnected) {
        throw new Error('IPFS service not available. Please check your configuration.');
      }

      setState(prev => ({ ...prev, uploadProgress: 25 }));

      // Upload to IPFS
      const { fileUpload, metadataUpload } = await ipfsService.uploadNFT(params.file, params.metadata);

      if (!fileUpload.success) {
        throw new Error(`File upload failed: ${fileUpload.error}`);
      }

      setState(prev => ({ ...prev, uploadProgress: 75 }));

      if (!metadataUpload.success) {
        throw new Error(`Metadata upload failed: ${metadataUpload.error}`);
      }

      setState(prev => ({ ...prev, uploadProgress: 100, isUploading: false, isMinting: true }));

      // Prepare minting parameters
      const mintValue = parseEther(params.mintPrice);
      
      // Set mint params - useEffect will automatically execute when ready
      setState(prev => ({ ...prev, isMinting: true, uploadProgress: 100 }));
      setMintParams({
        to: address,
        tokenURI: metadataUpload.url!,
        value: mintValue,
        contractAddress: params.contractAddress,
      });

    } catch (error) {
      console.error('NFT minting error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        isMinting: false, 
        error: errorMessage 
      }));
      toast.error(errorMessage);
    }
  }, [address, mintWrite]);

  /**
   * Mint multiple NFTs
   */
  const mintBatchNFTs = useCallback(async (params: BatchMintNFTParams) => {
    if (!address) {
      toast.error('Please connect your wallet');
      return;
    }

    try {
      setState(prev => ({ ...prev, isUploading: true, uploadProgress: 0, error: null }));

      // Test IPFS connection first
      const isConnected = await ipfsService.testConnection();
      if (!isConnected) {
        throw new Error('IPFS service not available. Please check your configuration.');
      }

      setState(prev => ({ ...prev, uploadProgress: 10 }));

      // Upload all files to IPFS
      const uploadResults = await ipfsService.uploadBatchNFTs(params.files, params.baseMetadata);

      // Check for upload failures
      const failedUploads = uploadResults.filter(result => 
        !result.fileUpload.success || !result.metadataUpload.success
      );

      if (failedUploads.length > 0) {
        throw new Error(`${failedUploads.length} uploads failed`);
      }

      setState(prev => ({ ...prev, uploadProgress: 100, isUploading: false, isMinting: true }));

      // Prepare batch minting parameters
      const tokenURIs = uploadResults.map(result => result.metadataUpload.url!);
      const totalValue = parseEther((parseFloat(params.mintPrice) * params.files.length).toString());

      // Set batch mint params - useEffect will automatically execute when ready
      setState(prev => ({ ...prev, isMinting: true, uploadProgress: 100 }));
      setBatchMintParams({
        to: address,
        tokenURIs,
        value: totalValue,
        contractAddress: params.contractAddress,
      });

    } catch (error) {
      console.error('Batch NFT minting error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        isMinting: false, 
        error: errorMessage 
      }));
      toast.error(errorMessage);
    }
  }, [address, batchMintWrite]);

  /**
   * Reset minting state
   */
  const resetState = useCallback(() => {
    setState({
      isUploading: false,
      isMinting: false,
      uploadProgress: 0,
      mintProgress: 0,
      error: null,
    });
    setMintParams(null);
    setBatchMintParams(null);
  }, []);

  return {
    // State
    isUploading: state.isUploading,
    isMinting: state.isMinting || isMintTxLoading || isBatchMintTxLoading,
    uploadProgress: state.uploadProgress,
    mintProgress: state.mintProgress,
    error: state.error || mintError?.message || batchMintError?.message,
    successData,
    
    // Actions
    mintNFT,
    mintBatchNFTs,
    resetState,
    clearSuccessData: () => setSuccessData(null),
    
    // Transaction hashes for tracking
    mintHash,
    batchMintHash,
  };
}

export type { NFTSuccessData };
